import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';

const MaxSnapPixelDistance = 15;

export function distanceBetweenInPx(
  viewer: AggregatedView['viewer'],
  p1: THREE.Vector3,
  p2?: THREE.Vector3
) {
  if (!p1 || !p2 || !viewer) return null;
  const p1Projected = Autodesk.Viewing.MeasureCommon.project(p1, viewer);
  const p2Projected = Autodesk.Viewing.MeasureCommon.project(p2, viewer);
  const point1 = new THREE.Vector3(p1Projected.x, p1Projected.y);
  const point2 = new THREE.Vector3(p2Projected.x, p2Projected.y);

  return point1.distanceTo(point2);
}

export const SNAPPING_LOCAL_STORAGE_KEY = 'sheets-viewer-v2-snapping-enabled';

function isSnappingEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const item = window.localStorage.getItem(SNAPPING_LOCAL_STORAGE_KEY);
    return item && item !== 'undefined' ? JSON.parse(item) : true;
  } catch {
    return true;
  }
}

export const getSnapPoint = (
  snapper: Autodesk.Viewing.Extensions.Snapping.Snapper,
  viewer: Autodesk.Viewing.Viewer3D
): THREE.Vector3 | null => {
  if (!isSnappingEnabled()) return null;
  const snapResult = snapper.getSnapResult();
  const { SnapType } = Autodesk.Viewing.MeasureCommon;

  switch (snapResult.geomType) {
    case SnapType.SNAP_VERTEX:
    case SnapType.SNAP_MIDPOINT:
    case SnapType.SNAP_INTERSECTION:
    case SnapType.SNAP_CIRCLE_CENTER:
    case SnapType.RASTER_PIXEL:
    case SnapType.SNAP_EDGE:
    case SnapType.SNAP_CIRCULARARC:
    case SnapType.SNAP_CURVEDEDGE: {
      const distance = distanceBetweenInPx(
        viewer,
        snapResult.geomVertex,
        snapResult.intersectPoint
      );

      return getSnapPointByDistance(
        snapResult.intersectPoint,
        snapResult.geomVertex,
        distance
      );
    }
    default:
      return snapResult.intersectPoint;
  }
};

export function getSnapPointByDistance(
  point: THREE.Vector3,
  snapTo: THREE.Vector3 | null,
  distance: number | null
) {
  const isWithinPixelDistance =
    distance !== null && distance <= MaxSnapPixelDistance;

  if (isWithinPixelDistance && snapTo) {
    return snapTo;
  }

  return point;
}

// Lazy-initialized snap types that support the snapping indicator
let SUPPORTED_SNAP_TYPES: Set<number> | null = null;

function getSupportedSnapTypes(): Set<number> {
  if (!SUPPORTED_SNAP_TYPES) {
    const { SnapType } = Autodesk.Viewing.MeasureCommon;
    SUPPORTED_SNAP_TYPES = new Set([
      SnapType.SNAP_VERTEX,
      SnapType.SNAP_MIDPOINT,
      SnapType.SNAP_INTERSECTION,
      SnapType.SNAP_CIRCLE_CENTER,
      SnapType.RASTER_PIXEL,
      SnapType.SNAP_EDGE,
      SnapType.SNAP_CIRCULARARC,
      SnapType.SNAP_CURVEDEDGE,
    ]);
  }
  return SUPPORTED_SNAP_TYPES;
}

/**
 * Get snap point info without modifying the cursor position.
 * Returns the snap point if within range, or null if no valid snap point.
 * This is used for showing the snapping indicator separately from the cursor.
 */
export const getSnapPointIndicator = (
  snapper: Autodesk.Viewing.Extensions.Snapping.Snapper,
  viewer: Autodesk.Viewing.Viewer3D
): THREE.Vector3 | null => {
  if (!isSnappingEnabled()) return null;

  const snapResult = snapper.getSnapResult();

  if (!getSupportedSnapTypes().has(snapResult.geomType)) {
    return null;
  }

  const distance = distanceBetweenInPx(
    viewer,
    snapResult.geomVertex,
    snapResult.intersectPoint
  );

  // Only return snap point if within distance threshold
  const isWithinRange =
    distance !== null &&
    distance <= MaxSnapPixelDistance &&
    snapResult.geomVertex;

  return isWithinRange ? snapResult.geomVertex : null;
};
